import React, { ChangeEventHandler, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { ParsedQs } from "qs";

import { Category } from "constants/events";
import { Query } from "custom-types/Query";
import { useEventTracker } from "hooks/useEventTracker";
import { useSelector } from "redux/hooks";
import { getIndicatedAddress } from "redux/selectors/location";
import parseNextRouterAsPath from "utils/parseNextRouterAsPath";
import { leaflyShopStringify } from "utils/qs";

import SearchInput from "components/SearchInput";
import TrackImpression from "components/TrackImpression";

import { FULFILLMENT_LABEL_MAP } from "../FulfillmentFilters/FulfillmentFilters";

const Header: React.FC = () => {
  const { publishEvent } = useEventTracker();
  const { asPath, push } = useRouter();
  const { path, query: parsedQs } = parseNextRouterAsPath<ParsedQs>(asPath);
  const indicatedAddress = useSelector(getIndicatedAddress);
  const { q: _q, page: _page, ...other } = parsedQs;
  const query = parsedQs.q as string;
  const appliedFilters = (parsedQs.filter as Query) || {};
  const selectedCategories = appliedFilters.category
    ? Array.isArray(appliedFilters.category)
      ? (appliedFilters.category as string[])
      : [appliedFilters.category]
    : null;
  const selectedFulfillments = parsedQs.fulfillment
    ? Array.isArray(parsedQs.fulfillment)
      ? (parsedQs.fulfillment as string[])
      : [parsedQs.fulfillment as string]
    : null;

  const setQuery = (value?: string) => {
    push({
      pathname: path,
      query: leaflyShopStringify({
        ...other,
        q: value,
      }),
    });
  };

  // * Local component state for input
  const [inputValue, setInputValue] = useState(query || "");
  // * Used to track the initial q parameter on page load
  const [initialSearch, _] = useState(query);

  const triggerSearch = (value: string) => {
    publishEvent({
      action: "click",
      category: Category.unifiedShoppingExperience,
      label: "search bar",
      searchedFor: value,
    });
    setQuery(value);
  };

  const changeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    // * Sync local input state with quey param, if the query param
    // * is updated elsewhere.
    setInputValue(query || "");
  }, [query]);

  return (
    <div className="mb-xl">
      <div className="container mt-xl">
        <TrackImpression
          category={Category.unifiedShoppingExperience}
          label="search button"
          customDimensions={{
            default_option: initialSearch,
          }}
        >
          <SearchInput
            inputProps={{
              className: "h-12",
              onChange: changeHandler,
              onKeyDown: (e) => {
                if (e.key && e.key !== "Enter") {
                  return;
                }

                triggerSearch(inputValue);
              },
              placeholder: "Search products",
              value: inputValue,
            }}
            clearButtonProps={{
              onClick: () => setQuery(),
            }}
            searchButtonProps={{
              onClick: () => triggerSearch(inputValue),
            }}
          />
        </TrackImpression>
        <h3 className="mt-lg">
          {/* Category portion of header string */}
          {selectedCategories ? (
            <>
              {"Best "}
              {selectedCategories.map((c) => c.toLowerCase()).join(" and ")}
            </>
          ) : (
            <>{"Shop cannabis products"}</>
          )}
          {/* Fulfillment portion of header string */}
          {selectedFulfillments ? (
            <>
              {" for "}
              {selectedFulfillments
                .map((c) => FULFILLMENT_LABEL_MAP[c].toLowerCase())
                .join(" or ")}
            </>
          ) : (
            <></>
          )}
          {/* Location portion of header string -- if the cookie has not loaded yet it will fallback to our default of Seattle, WA */}
          {indicatedAddress ? (
            <>
              {" near "}
              <span>{indicatedAddress}</span>
            </>
          ) : (
            <></>
          )}
        </h3>
      </div>
    </div>
  );
};

export default Header;
